import { lengthOf, type Note, type Plan } from "./score.ts";

export type MakesAudio = () => AudioContext;

export type Device = {
  wake(): void;

  awake(): boolean;

  silent(): boolean;

  play(plan: Plan, gain: number): void;

  hush(): void;
};

const A_TICK_BUFFER = 0.1;

const NEARLY_NOTHING = 0.0001;

export function silentIn(state: AudioContextState | null, woken: boolean): boolean {
  if (!woken) return false;
  return state === null || state !== "running";
}

function tickBuffer(audio: AudioContext): AudioBuffer {
  const frames = Math.max(1, Math.floor(audio.sampleRate * A_TICK_BUFFER));
  const buffer = audio.createBuffer(1, frames, audio.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let frame = 0; frame < frames; frame += 1) channel[frame] = Math.random() * 2 - 1;
  return buffer;
}

function envelope(level: GainNode, note: Note, at: number, plan: Plan, gain: number): void {
  const top = Math.max(NEARLY_NOTHING, note.gain * gain);
  const { attack, release } = plan.shape;
  level.gain.setValueAtTime(NEARLY_NOTHING, at);
  level.gain.linearRampToValueAtTime(top, at + attack);
  level.gain.setValueAtTime(top, at + note.seconds);
  level.gain.exponentialRampToValueAtTime(NEARLY_NOTHING, at + note.seconds + release);
}

function pitched(audio: AudioContext, note: Note, at: number, plan: Plan, into: AudioNode): void {
  const ends = at + note.seconds + plan.shape.release;
  const detunes = plan.shape.detune === 0 ? [0] : [-plan.shape.detune, plan.shape.detune];
  for (const cents of detunes) {
    const tone = audio.createOscillator();
    tone.type = plan.shape.wave;
    tone.detune.value = cents;
    tone.frequency.setValueAtTime(note.hz, at);
    if (note.toHz !== undefined) tone.frequency.linearRampToValueAtTime(note.toHz, at + note.seconds);
    tone.connect(into);
    tone.start(at);
    tone.stop(ends);
  }
}

function ticked(audio: AudioContext, note: Note, at: number, buffer: AudioBuffer, into: AudioNode): void {
  const noise = audio.createBufferSource();
  noise.buffer = buffer;
  noise.connect(into);
  noise.start(at);
  noise.stop(at + note.seconds + A_TICK_BUFFER);
}

export function makeDevice(makes: MakesAudio): Device {
  let audio: AudioContext | null = null;
  let buffer: AudioBuffer | null = null;
  let woken = false;

  let sounding: Array<{ out: GainNode; until: number }> = [];

  const wake = (): void => {
    woken = true;
    try {
      audio ??= makes();

      if (audio.state !== "running") void audio.resume().catch(() => undefined);
    } catch {
      audio = null;
    }
  };

  const play = (plan: Plan, gain: number): void => {
    const held = audio;

    if (!held || held.state === "closed" || gain <= 0) return;
    const at = held.currentTime;
    const out = held.createGain();
    out.gain.value = 1;
    out.connect(held.destination);
    sounding = sounding.filter((one) => one.until > at);
    sounding.push({ out, until: at + lengthOf(plan) });
    if (plan.shape.noise) buffer ??= tickBuffer(held);

    for (const note of plan.notes) {
      const level = held.createGain();
      envelope(level, note, at + note.at, plan, gain);
      let into: AudioNode = level;
      if (plan.shape.filter) {
        const filter = held.createBiquadFilter();
        filter.type = plan.shape.filter.kind;
        filter.frequency.value = note.hz * plan.shape.filter.of;
        level.connect(filter);
        filter.connect(out);
        into = level;
      } else {
        level.connect(out);
      }
      if (plan.shape.noise && buffer) ticked(held, note, at + note.at, buffer, into);
      else pitched(held, note, at + note.at, plan, into);
    }
  };

  return {
    wake,
    awake: () => audio !== null,
    silent: () => silentIn(audio?.state ?? null, woken),
    play,
    hush: () => {
      for (const one of sounding) {
        try {
          one.out.disconnect();
        } catch {}
      }
      sounding = [];
    },
  };
}

export const theDevice: Device = makeDevice(() => new AudioContext());
